import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Button } from "../../../components/SystemComponent/Button";
import { Modal } from "../../../components/SystemComponent/Modal";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { MdAdd, MdEdit, MdDelete, MdLayers } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const ModulePackageManage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const api = useMemo(() => createApi(), []);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [durationInMonths, setDurationInMonths] = useState("");

  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.package.getByModuleId(Number(moduleId));
    const list = r?.data?.data ?? r?.data ?? [];
    setData(Array.isArray(list) ? list : []);
    setLoading(false);
  }, [api, moduleId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditing(null);
    setTitle("");
    setDescription("");
    setPrice("");
    setDurationInMonths("");
    setError(null);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setTitle(row.title ?? "");
    setDescription(row.description ?? "");
    setPrice(String(row.price ?? ""));
    setDurationInMonths(
      row.durationInMonths != null ? String(row.durationInMonths) : ""
    );
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (!price || isNaN(Number(price))) {
      setError("Price must be a valid number");
      return;
    }

    if (
      durationInMonths &&
      (!Number.isInteger(Number(durationInMonths)) ||
        Number(durationInMonths) < 0)
    ) {
      setError("Duration must be a positive integer");
      return;
    }

    const payload = {
      moduleId: Number(moduleId),
      title: title.trim(),
      description: description.trim(),
      price: Number(price),
      durationInMonths: durationInMonths ? Number(durationInMonths) : null,
    };

    if (editing) {
      await api.package.update(editing.id, payload);
    } else {
      await api.package.create(payload);
    }

    setOpen(false);
    resetForm();
    load();
  };

  const columns = [
    {
      key: "title",
      title: "Title",
      accessor: (r: any) => r.title,
    },
    {
      key: "price",
      title: "Price",
      accessor: (r: any) => r.price,
    },
    {
      key: "duration",
      title: "Duration (Months)",
      accessor: (r: any) => r.durationInMonths ?? "-",
    },
    {
      key: "actions",
      title: "Actions",
      render: (row: any) => (
        <GroupButton
          items={[
            {
              key: "items",
              icon: MdLayers,
              color: "#059669",
              onClick: () =>
                navigate(
                  `/dashboard/module/${moduleId}/package/${row.id}/items`
                ),
            },
            {
              key: "edit",
              icon: MdEdit,
              color: "#2563EB",
              onClick: () => openEdit(row),
            },
            {
              key: "delete",
              icon: MdDelete,
              color: "#DC2626",
              onClick: async () => {
                if (
                  !window.confirm(
                    "Bu paketi pasife almak istediğinize emin misiniz? (Paket pasife çekilecek ve web tarafında görünmeyecektir)"
                  )
                ) {
                  return;
                }
                try {
                  const res = await api.package.delete(row.id);
                  if (res && !res.ok) {
                    alert((res as any)?.message || "Paket pasife alınırken hata oluştu.");
                  }
                  load();
                } catch (e: any) {
                  alert(e?.message || "Beklenmeyen hata oluştu");
                }
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Button icon={MdAdd} onClick={openCreate}>
        Create Package
      </Button>

      <Table
        tableKey="module-packages"
        loading={loading}
        columns={columns}
        data={data}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>
          {editing ? "Update Package" : "Create Package"}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={F.group}>
            <label style={F.label}>Title</label>
            <input
              style={F.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Package title"
            />
          </div>

          <div style={F.group}>
            <label style={F.label}>Description</label>
            <textarea
              style={F.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <div style={F.group}>
            <label style={F.label}>Price</label>
            <input
              style={F.input}
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div style={F.group}>
            <label style={F.label}>Duration (Months)</label>
            <input
              style={F.input}
              type="number"
              step="1"
              value={durationInMonths}
              onChange={(e) => setDurationInMonths(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {error && <div style={F.error}>{error}</div>}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const F = {
  group: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    padding: "0 14px",
    fontSize: 14,
    outline: "none",
  },
  textarea: {
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    padding: 14,
    fontSize: 14,
    minHeight: 90,
    resize: "vertical" as const,
    outline: "none",
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
